import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import {
  PatchCustomerSettingsData,
  Purpose,
} from 'src/app/api/autogenerated/types.gen';
import { DocumentDetails } from 'src/app/types/DocumentDetails';
import { InvoiceItem } from 'src/app/types/InvoiceItem';
import { environment } from '../../../../environments/environment';
import {
  CreateInvoicePreviewRequestContent,
  CreateInvoiceRequestContent,
  Currency,
  InvoiceRequestPayload,
  InvoicesModel,
  PayoutModel,
  PayoutRequest,
  PostPayoutsRequestContent,
  UpdateInvoiceByIdRequestContent,
} from '../../../api/autogenerated';
import { isLegacyVendorBillingProfile } from '../../../shared/vendor-ui/vendor-ui-capability.util';
import { Invoice } from '../../../types/Invoice';
import {
  InvoiceActivity,
  InvoiceActivityType,
} from '../../../types/InvoiceActivity';
import { InvoiceLog } from '../../../types/invoiceLog';
import { InvoiceStatus } from '../../../types/InvoiceStatus';
import { PaymentMethodDetails } from '../../../types/PaymentDetails';
import { Payout } from '../../../types/Payout';
import {
  PayoutFrequencyPreferences,
  PayoutsListingData,
} from '../../../types/PayoutsListingData';
import { Profile } from '../../../types/Profile';
import { InvoicesListingFilters } from '../../../types/requests/InvoicesListingFilters';
import { PayoutPreferencesRequest } from '../../../types/requests/PayoutPreferencesRequest';
import {
  PayoutsListingRequest,
  PayoutsListingRequestType,
} from '../../../types/requests/PayoutsListingRequest';
import {
  InvoicesWithWithdrawalAmounts,
  WithdrawlAgainstInvoicesRequest,
} from '../../../types/requests/WithdrawlAgainstInvoicesRequest';
import { WithdrawlAgainstInvoicesResponse } from '../../../types/responses/WithdrawlAgainstInvoicesResponse';
import { SharedProfileData } from '../../data/shared-profile-data';
import { IInvoiceManager } from '../IInvoiceManager';
import {
  mockPayoutFrequencyPreferencesDaily,
  mockPayouts,
} from '../mock/mock-data';
import { OpenApiService } from '../open-api.service';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService implements IInvoiceManager {
  payouts: Payout[] = mockPayouts;
  payoutsFrequencyPreferences: PayoutFrequencyPreferences =
    mockPayoutFrequencyPreferencesDaily;

  options = {
    headers: {
      'X-Organization-ID': environment.organization_id,
    },
  };

  constructor(
    private sharedProfileData: SharedProfileData,
    private openapiService: OpenApiService,
  ) {}

  private getInvoiceStatus(invoice: InvoicesModel): InvoiceStatus {
    switch (invoice.status) {
      case 'DRAFT':
        return InvoiceStatus.Draft;
      case 'UNPAID':
        return InvoiceStatus.Unpaid;
      case 'PAID':
        return InvoiceStatus.Paid;
      case 'OVERDUE':
        return InvoiceStatus.Overdue;
      case 'PARTIALLY_PAID':
        return InvoiceStatus.PartialPaid;
      case 'REJECTED':
        return InvoiceStatus.Rejected;
      case 'VERIFYING':
        return InvoiceStatus.Verifying;
      case 'CANCELLED':
        return InvoiceStatus.Revoked;
      default:
        return InvoiceStatus.Canceled;
    }
  }

  setInvoiceItems(invoice: InvoicesModel): InvoiceItem[] {
    let invoiceItems: InvoiceItem[] = [];

    invoice.items.forEach((item) => {
      invoiceItems.push({
        itemDescription: item.item,
        quantity: item.quantity,
        price: {
          value: item.price,
          currency: this.normalizeCurrencyCode(invoice.currency),
        },
        total: item.total,
        tax: invoice.vat,
      });
    });

    return invoiceItems;
  }

  private convertInvoicesFromApiToFrontend(
    invoices: InvoicesModel[],
  ): Invoice[] {
    let invoiceList: Invoice[] = [];
    invoices.forEach((invoice) => {
      const currency = this.normalizeCurrencyCode(invoice.currency);
      invoiceList.push({
        id: invoice.id,
        billingStartDateUTC: invoice.billing_start_date,
        billingEndDateUTC: invoice.billing_end_date,
        invoiceDate: invoice.invoice_date,
        dueDate: invoice.invoice_due_date,
        invoiceItems: this.setInvoiceItems(invoice),
        totalAmount: {
          value: invoice.total,
          currency,
        },
        dueAmount: {
          value: invoice.due_amount,
          currency,
        },
        status: this.getInvoiceStatus(invoice),
        purposeOfTransaction: invoice.purpose_of_transaction,
        isValid: false,
        partnerId: invoice.partner_id,
        invoiceNote: invoice.invoice_note,
        invoiceDocument: invoice.invoice_document,
        invoiceNo: invoice.invoice_number,
        billFromAddress: '',
        billToAddress: '',
        billFrom: '',
        billTo: '',
        profilePath: '',
        transactionType: invoice.transaction_type,
        vatPercentage: invoice.vat_percentage,
        vat: invoice.vat,
        purposeCode: invoice.purpose_code,
        subTotal: invoice.sub_total,
        uploadedInvoice: this.getUploadedInvoiceDocument(invoice),
      });
    });
    return invoiceList;
  }

  private getInvoiceTimelineEvents(invoice: InvoicesModel) {
    let timelineEvents: InvoiceActivity[] = [];
    invoice.timeline?.forEach((event) => {
      timelineEvents.push({
        aTitle: event.title,
        aAnnotation: event.message,
        aType: event.type as InvoiceActivityType,
        aDate: new Date(event.time),
      });
    });
    return timelineEvents;
  }

  private getUploadedInvoiceDocument(
    invoice: InvoicesModel,
  ): DocumentDetails | undefined {
    if (invoice.support_documents && invoice.support_documents.length > 0) {
      // filter for the ADDITIONAL_INVOICE_DOCUMENT
      let supportDocument = invoice.support_documents.filter(
        (doc) => doc.purpose === 'ADDITIONAL_INVOICE_DOCUMENT',
      );

      return {
        fileId: supportDocument[0].document_id,
        name: supportDocument[0].name,
        size: supportDocument[0].size,
        mediaType: supportDocument[0].mime_type,
        type: supportDocument[0].purpose,
      };
    }
    return undefined;
  }

  async getInvoiceById(invoiceId: string): Promise<Invoice> {
    let dataFromResponse = await this.openapiService.getInvoiceById(invoiceId);

    return this.convertInvoicesFromApiToFrontend([dataFromResponse.data])[0];
  }

  async getInvoiceByPayoutId(payoutId: string): Promise<Invoice> {
    const user: Profile = this.sharedProfileData.getUserProfile();
    let dataFromResponse = await this.openapiService.getInvoiceByPayoutId(
      user.businessId,
      payoutId,
    );

    return this.convertInvoicesFromApiToFrontend([dataFromResponse.data])[0];
  }

  async getBankAccountByPayoutId(
    payoutId: string,
  ): Promise<PaymentMethodDetails> {
    const user: Profile = this.sharedProfileData.getUserProfile();
    let dataFromResponse = await this.openapiService.getBankAccountByPayoutId(
      user.businessId,
      payoutId,
    );

    if (!dataFromResponse.success) {
      throw new Error(dataFromResponse.error);
    }

    const indianBankAccount = dataFromResponse.data;

    const paymentMethodDetails: PaymentMethodDetails = {
      id: indianBankAccount.id,
      accountName: indianBankAccount.account_name || '',
      accountNumber: indianBankAccount.account_no || '',
      accountType: indianBankAccount.account_type || '',
      ifscCode: indianBankAccount.ifsc_code || '',
      bankName: indianBankAccount.bank_name || '',
      bankAddress: indianBankAccount.bank_branch || '',
      paymentMethodType: 'INDIAN_BANK_ACCOUNT',
      achRouting: '',
      isDefault: indianBankAccount.is_default === true,
    };

    return paymentMethodDetails;
  }

  // todo: Change this file type to FileType and use this function from the commons
  private getPurposeType(type: String): Purpose {
    switch (type) {
      case 'ADDITIONAL_INVOICE_DOCUMENT':
        return 'ADDITIONAL_INVOICE_DOCUMENT';

      default:
        throw new Error('Unsupported file type');
    }
  }

  private normalizeCurrencyCode(currency?: string | null): Currency {
    const normalized = currency?.trim().toUpperCase();
    return (normalized || 'USD') as Currency;
  }

  private resolveInvoiceCurrency(invoice: Partial<Invoice>): Currency {
    const profile = this.sharedProfileData.getUserProfile();
    if (isLegacyVendorBillingProfile(profile)) {
      return 'USD' as Currency;
    }

    const currency =
      invoice.totalAmount?.currency ||
      invoice.invoiceItems?.find((item) => item.price?.currency)?.price
        ?.currency;

    return this.normalizeCurrencyCode(currency);
  }

  async createInvoice(invoice: Partial<Invoice>): Promise<string> {
    const user: Profile = this.sharedProfileData.getUserProfile();
    const invoiceCurrency = this.resolveInvoiceCurrency(invoice);

    let body: CreateInvoiceRequestContent = {
      invoice_id: invoice?.id,
      invoice_data: {
        billing_start_date:
          invoice.billingStartDateUTC !== ''
            ? invoice.billingStartDateUTC
            : undefined,
        billing_end_date:
          invoice.billingEndDateUTC !== ''
            ? invoice.billingEndDateUTC
            : undefined,
        invoice_date:
          invoice.invoiceDate !== '' ? invoice.invoiceDate : undefined,
        due_date: invoice.dueDate !== '' ? invoice.dueDate : undefined,
        total_amount: {
          value: invoice.totalAmount?.value,
          currency: invoiceCurrency,
        },
        due_amount: {
          value: invoice.totalAmount?.value,
          currency: invoiceCurrency,
        },
        status: invoice.status,
        purpose_of_transaction: invoice.purposeOfTransaction,
        is_valid: invoice.isValid,
        bill_from: invoice.billFrom,
        bill_to: invoice.billTo,
        profile_path: invoice.profilePath,
        invoice_due_date: invoice.dueDate !== '' ? invoice.dueDate : undefined,
        invoice_number: invoice.invoiceNo,
        currency: invoiceCurrency,
        items: [],
        sub_total: invoice.subTotal,
        vat: invoice.vat,
        total: invoice.totalAmount?.value,
        purpose_code: invoice.purposeCode,
        partner_id: invoice.partnerId,
        invoice_note: invoice.invoiceNote,
        transaction_type: invoice.transactionType,
        vat_percentage: invoice.vatPercentage,
        support_documents: [],
      } as InvoiceRequestPayload,
    };
    invoice.invoiceItems?.forEach(function (item) {
      if (item == null) return;
      if (item.price?.value == null || item.price?.value == 0) return;
      if (item.total == null) return;
      if (item.quantity == null) return;

      body.invoice_data.items.push({
        item: item.itemDescription,
        quantity: item.quantity,
        price: item.price.value,
        total: item.total,
      });
    });

    if (invoice.uploadedInvoice) {
      console.log(invoice.uploadedInvoice);
      body.invoice_data.support_documents.push({
        document_id: invoice.uploadedInvoice.fileId,
        name: invoice.uploadedInvoice.name,
        size: invoice.uploadedInvoice.size,
        mime_type: invoice.uploadedInvoice.mediaType,
        purpose: this.getPurposeType(
          invoice.uploadedInvoice.type ? invoice.uploadedInvoice.type : '',
        ),
      });
    }

    let dataFromInvoiceCreation = await this.openapiService.createInvoice(
      user.businessId,
      body,
    );
    if (dataFromInvoiceCreation.success) {
      return dataFromInvoiceCreation.data.id;
    } else {
      return '';
    }
  }

  async updateInvoice(
    invoiceId: string,
    sendCopyToUser: boolean,
  ): Promise<Invoice | null> {
    let updateInvoiceByIdRequestContent: UpdateInvoiceByIdRequestContent = {
      operation: 'PUBLISH',
      operation_inputs: {
        send_me_a_copy: sendCopyToUser,
      },
    };
    let dataFromResponse = await this.openapiService.updateInvoiceById(
      invoiceId,
      updateInvoiceByIdRequestContent,
    );
    if (!dataFromResponse.success) {
      throw new Error(dataFromResponse.error);
    }
    return this.convertInvoicesFromApiToFrontend([dataFromResponse.data])[0];
  }

  async createInvoicePreview(
    invoice: Partial<Invoice>,
  ): Promise<{ success: boolean; data: Blob; error: string }> {
    const user: Profile = this.sharedProfileData.getUserProfile();
    const invoiceCurrency = this.resolveInvoiceCurrency(invoice);

    let body: CreateInvoicePreviewRequestContent = {
      invoice_data: {
        billing_start_date:
          invoice?.billingStartDateUTC && invoice?.billingStartDateUTC !== ''
            ? invoice?.billingStartDateUTC
            : undefined,
        billing_end_date:
          invoice?.billingEndDateUTC && invoice?.billingEndDateUTC !== ''
            ? invoice?.billingEndDateUTC
            : undefined,
        invoice_date:
          invoice?.invoiceDate && invoice?.invoiceDate !== ''
            ? invoice?.invoiceDate
            : undefined,
        invoice_due_date:
          invoice?.dueDate && invoice?.dueDate !== ''
            ? invoice?.dueDate
            : undefined,
        invoice_number: invoice?.invoiceNo ? invoice?.invoiceNo : '',
        currency: invoiceCurrency,

        total: invoice?.totalAmount?.value ? invoice?.totalAmount?.value : 0,
        purpose_of_transaction: invoice?.purposeOfTransaction
          ? invoice?.purposeOfTransaction
          : '',
        purpose_code: invoice?.purposeCode ? invoice?.purposeCode : '',
        invoice_note: invoice?.invoiceNote ? invoice?.invoiceNote : '',
        transaction_type: this.getTransactionType(invoice),
        vat: invoice.vat || 0,
        sub_total: invoice.subTotal || 0,
        items: [],
        vat_percentage: invoice.vatPercentage || 0,
        partner_id: invoice.partnerId || '',
        support_documents: [],
      } as InvoiceRequestPayload,
    };

    invoice.invoiceItems?.forEach(function (item) {
      if (item == null) return;
      if (item.price?.value == null || item.price?.value == 0) return;
      if (item.total == null) return;
      if (item.quantity == null) return;

      body.invoice_data.items.push({
        item: item.itemDescription,
        quantity: item.quantity,
        price: item.price.value,
        total: item.total,
      });
    });

    let dataFromResponse = await this.openapiService.createInvoicePreview(
      user.businessId,
      body,
    );

    return dataFromResponse;
  }

  async revokeInvoice(invoiceId: string): Promise<boolean> {
    let updateInvoiceRequestBody: UpdateInvoiceByIdRequestContent = {
      operation: 'REVOKE',
    };
    let dataFromResponse = await this.openapiService.updateInvoiceById(
      invoiceId,
      updateInvoiceRequestBody,
    );
    if (!dataFromResponse.success) {
      throw new Error(dataFromResponse.error);
    }
    return true;
  }

  async deleteInvoice(invoiceNo: string): Promise<boolean> {
    return true;
  }

  async sendAReminderForInvoice(invoiceNo: string): Promise<boolean> {
    console.log(invoiceNo);
    return true;
  }

  async sendOverdueRemindersForInvoices(
    invoiceIds: string[],
  ): Promise<boolean> {
    const result = await this.openapiService.postInvoiceOverdueReminder({
      invoice_ids: invoiceIds,
    });
    if (!result.success) {
      throw new Error(result.error || 'Failed to send overdue reminders');
    }
    return true;
  }

  async updateInvoiceStatus(
    invoiceNo: string,
    status: InvoiceStatus,
  ): Promise<Invoice | null> {
    return null;
  }

  async duplicateInvoice(invoiceNo: string): Promise<Invoice | null> {
    return null;
  }

  async getInvoiceLog(invoiceNo: string): Promise<InvoiceLog | null> {
    return null;
  }

  async updateInvoiceDueDate(
    invoiceNo: string,
    newDueDate: string,
  ): Promise<Invoice | null> {
    return null;
  }

  private getPayoutStatus(invoice: PayoutModel): Payout['payoutStatus'] {
    switch (invoice.status) {
      case 'INITIALIZED':
        return 'initialized';
      case 'PROCESSING':
        return 'processing';
      case 'SETTLED':
        return 'settled';
      case 'HOLD':
        return 'hold';
      default:
        return 'failed';
    }
  }

  async getPayoutsListData(
    customerId: string,
  ): Promise<PayoutsListingData['payouts']> {
    console.log(Number((new Date().getTime() / 1000).toFixed(0)));
    let payoutsList: PayoutsListingData['payouts'] = [];

    // Check for stored initiation data
    const lastInitiationData = localStorage.getItem('lastPayoutInitiation');
    let shouldRemoveInitiation = true;

    let dataFromResponse = await this.openapiService.getPayouts(customerId, {
      from_time: Math.floor(
        (new Date().getTime() - 365 * 24 * 60 * 60 * 1000) / 1000,
      ),
      to_time: Number((new Date().getTime() / 1000).toFixed(0)),
    });

    const allPayouts = dataFromResponse.data;

    if (lastInitiationData) {
      const { initiationTime, withdrawalAmount, withdrawalAmountInr } =
        JSON.parse(lastInitiationData);

      // Convert initiationTime to IST for comparison
      const initiationMoment = moment(initiationTime).tz('Asia/Kolkata');

      // Check if any existing payout matches our initiation
      const matchingPayout = allPayouts.find((payout) => {
        if (!payout.initiation_date || !payout.gross_amount?.amount)
          return false;

        // Parse payout date as IST
        const payoutMoment = moment.tz(payout.initiation_date, 'Asia/Kolkata');

        const timeDiff = Math.abs(
          payoutMoment.diff(initiationMoment, 'minutes'),
        );
        return timeDiff <= 5 && payout.gross_amount.amount === withdrawalAmount;
      });

      // If no matching payout found and within 5 minutes, add initialized payout
      if (!matchingPayout) {
        const currentMoment = moment().tz('Asia/Kolkata');
        const minutesSinceInitiation = currentMoment.diff(
          initiationMoment,
          'minutes',
        );

        if (minutesSinceInitiation <= 5) {
          shouldRemoveInitiation = false;
          const initializedPayout: Payout = {
            id: 'temp-' + initiationTime,
            businessId: customerId,
            payoutReferenceId: 'TEMP-' + initiationTime.toString().slice(-6),
            payoutStatus: 'initialized',
            grossPayoutAmount: withdrawalAmount,
            netPayoutAmount: withdrawalAmount,
            finalSettledAmount: withdrawalAmountInr,
            grossOrNetPayoutCurrency: 'USD',
            finalSettledCurrency: 'INR',
            sentDateUTC: initiationMoment.toISOString(),
            expectedReceiveDateUTC: initiationMoment
              .add(5, 'minutes')
              .toISOString(),
          };
          payoutsList.push(initializedPayout);
        }
      }

      // Remove initiation data if needed
      if (shouldRemoveInitiation) {
        console.log('Removing initiation data');
        localStorage.removeItem('lastPayoutInitiation');
      }
    }

    // Add all other payouts after the initialized one (if any)
    allPayouts.forEach((payout) => {
      payoutsList.push({
        id: payout.id,
        businessId: '',
        clientBusinessId: '',
        partnerId: '',
        invoiceId: '',
        sentDateUTC: payout?.initiation_date ? payout?.initiation_date : '',
        expectedReceiveDateUTC: payout.arrival,
        payoutReferenceId: payout?.reference_id ? payout?.reference_id : '',
        grossPayoutAmount: payout?.gross_amount?.amount,
        netPayoutAmount: payout.net_amount?.amount,
        grossOrNetPayoutCurrency: payout?.gross_amount?.currency,
        finalSettledAmount: payout.settled_amount?.amount,
        finalSettledCurrency: payout.settled_amount?.currency,
        bankAccountNumber: '',
        payoutFee: 0,
        payoutAmount: 0,
        payoutStatus: this.getPayoutStatus(payout),
        receiptFileId: payout.receipt_file_id ? payout.receipt_file_id : '',
        uniqueTransactionReference: payout.unique_transaction_reference
          ? payout.unique_transaction_reference
          : '',
      });
    });

    return payoutsList;
  }

  async getPayoutsSummaryData(
    customerId: string,
  ): Promise<PayoutsListingData['dayWisePayoutSummary']> {
    console.log(Number((new Date().getTime() / 1000).toFixed(0)));
    let dayWisePayoutSummary: PayoutsListingData['dayWisePayoutSummary'] = [];
    let dataFromResponse =
      await this.openapiService.getAnalyticsForPayoutsGraph(customerId, {
        from_date: new Date(
          new Date().setFullYear(new Date().getFullYear() - 1),
        )
          .toISOString()
          .split('T')[0],
        to_date: new Date(new Date().setDate(new Date().getDate() + 1))
          .toISOString()
          .split('T')[0],
      });
    const allPayoutsAnalytics = dataFromResponse.data;
    allPayoutsAnalytics.forEach((analytic) => {
      dayWisePayoutSummary.push({
        date: analytic.date,
        totalAmount: analytic.settled_amount.amount,
        totalPayouts: analytic.settled_count,
        payoutCurrency: analytic.settled_amount.currency,
      });
    });
    return dayWisePayoutSummary;
  }

  async getPayouts(
    payoutsListingRequest: PayoutsListingRequest,
  ): Promise<PayoutsListingData> {
    let response: PayoutsListingData = {};
    const promises: Promise<void>[] = [];

    // Create promises for each requested type
    if (
      payoutsListingRequest.requestType.includes(
        PayoutsListingRequestType.DAY_WISE_PAYOUT_SUMMARY,
      )
    ) {
      promises.push(
        this.getPayoutsSummaryData(payoutsListingRequest.businessId).then(
          (data) => {
            response.dayWisePayoutSummary = data;
          },
        ),
      );
    }

    if (
      payoutsListingRequest.requestType.includes(
        PayoutsListingRequestType.PAYOUTS_LISTING,
      )
    ) {
      promises.push(
        this.getPayoutsListData(payoutsListingRequest.businessId).then(
          (data) => {
            response.payouts = data;
          },
        ),
      );
    }

    if (
      payoutsListingRequest.requestType.includes(
        PayoutsListingRequestType.CURRENT_PAYOUTS_PREFERENCES,
      )
    ) {
      promises.push(
        this.openapiService
          .getCustomerSettings(payoutsListingRequest.businessId)
          .then((payoutPreferenceResponse) => {
            if (
              payoutPreferenceResponse.data.payout_preferences !== undefined
            ) {
              response.payoutFrequencyPreferences = {
                frequency:
                  payoutPreferenceResponse.data.payout_preferences.payout_frequency?.toLowerCase() as
                    | 'daily'
                    | 'monthly'
                    | 'weekly',
                dayOfWeek:
                  payoutPreferenceResponse.data.payout_preferences?.weekly_anchor?.toLowerCase() as
                    | 'monday'
                    | 'tuesday'
                    | 'wednesday'
                    | 'thursday'
                    | 'friday',
                dayOfMonth:
                  payoutPreferenceResponse.data.payout_preferences
                    ?.monthly_anchor,
              };
            }
          }),
      );
    }

    // Wait for all promises to resolve
    await Promise.all(promises);
    return response;
  }

  async updatePayoutPreferences(
    payoutPreferencesRequest: PayoutPreferencesRequest,
  ): Promise<boolean> {
    // return Promise.resolve(false);

    let patchCustomerSettingsData: PatchCustomerSettingsData['body'] = {
      payout_frequency: payoutPreferencesRequest.frequency,

      ...(payoutPreferencesRequest.frequency === 'weekly' &&
        payoutPreferencesRequest.dayOfWeek && {
          weekly_anchor: payoutPreferencesRequest.dayOfWeek,
        }),

      ...(payoutPreferencesRequest.frequency === 'monthly' &&
        payoutPreferencesRequest.dayOfMonth && {
          monthly_anchor: payoutPreferencesRequest.dayOfMonth,
        }),
    };

    let responseFromApi = await this.openapiService.patchCustomerSettings(
      payoutPreferencesRequest.businessId,
      patchCustomerSettingsData,
    );
    console.log(responseFromApi);
    return true; // Indicate success
  }

  async updateLogoId(logoId: string): Promise<boolean> {
    console.log('Preparing to call BE service');
    const user: Profile = this.sharedProfileData.getUserProfile();
    let patchCustomerSettingsData: PatchCustomerSettingsData['body'] = {
      logo_id: logoId,
    };
    let responseFromApi = await this.openapiService.patchCustomerSettings(
      user.businessId,
      patchCustomerSettingsData,
    );
    console.log(responseFromApi);
    return true;
  }

  async getInvoices(filters: InvoicesListingFilters): Promise<Invoice[]> {
    const user: Profile = this.sharedProfileData.getUserProfile();
    let dataFromResponse = await this.openapiService.getInvoices(
      user.businessId,
      {
        partner_ids:
          filters.partnerIds.length > 0 ? filters.partnerIds : undefined,
      },
    );
    let invoices = this.convertInvoicesFromApiToFrontend(dataFromResponse.data);
    console.log(invoices);
    if (filters.statuses != undefined && filters.statuses.length > 0) {
      invoices = invoices.filter((value, index) => {
        return (
          value.status != undefined &&
          filters.statuses != undefined &&
          filters.statuses.indexOf(value.status) > -1
        );
      });
    }
    if (filters.currency) {
      const currency = filters.currency.toUpperCase();
      invoices = invoices.filter(
        (invoice) => invoice.totalAmount?.currency?.toUpperCase() === currency,
      );
    }
    return invoices;
  }

  async createWithdrawalAgainstInvoices(
    request: WithdrawlAgainstInvoicesRequest,
  ): Promise<WithdrawlAgainstInvoicesResponse> {
    console.log(request);
    let payoutRequests: PayoutRequest[] = [];
    request.invoicesWithWithdrawalAmounts.forEach(function (item) {
      payoutRequests.push({
        invoice_id: item.invoiceId,
        reconciliation_amount: item.withdrawalAmount,
        bank_id: request.paymentMethodDetailsId,
      });
    });

    const payoutRequestBody: PostPayoutsRequestContent = {
      payout_requests: payoutRequests,
    };

    let responseFromApi = await this.openapiService.postPayouts(
      request.businessId,
      payoutRequestBody,
    );
    console.log(responseFromApi);

    // Store initiation data in localStorage if the API call was successful
    if (
      responseFromApi.success &&
      responseFromApi.data.some((item) => item.status === 'SUCCESS')
    ) {
      const initiationData = {
        initiationTime: new Date().getTime(),
        withdrawalAmount: request.invoicesWithWithdrawalAmounts.reduce(
          (sum, item) => sum + item.withdrawalAmount,
          0,
        ),
        withdrawalAmountInr: request.invoicesWithWithdrawalAmounts.reduce(
          (sum, item) => sum + item.withdrawalAmountInr,
          0,
        ),
      };
      localStorage.setItem(
        'lastPayoutInitiation',
        JSON.stringify(initiationData),
      );
    }

    let response: WithdrawlAgainstInvoicesResponse = {
      numberOfInvoicesPaid: 0,
      payoutFinalAmount: 0,
      payoutFinalCurrency: 'INR',
      payoutInitiatedAmount: 0,
      payoutInitiatedCurrency: 'USD',
      payoutInitiatedDateUTC: new Date().toISOString(),
      status: false,
    };
    responseFromApi.data.forEach((value) => {
      if (value.status === 'SUCCESS') {
        response.status = true;
        if (response.numberOfInvoicesPaid !== undefined) {
          response.numberOfInvoicesPaid += 1;
        }
        if (
          response.payoutInitiatedAmount !== undefined &&
          response.payoutFinalAmount !== undefined
        ) {
          let res = value.invoice_id
            ? request.invoicesWithWithdrawalAmounts.find(
                (item: InvoicesWithWithdrawalAmounts) =>
                  item.invoiceId == value.invoice_id,
              )
            : undefined;
          if (res != undefined) {
            response.payoutInitiatedAmount += res.withdrawalAmount;
            response.payoutFinalAmount += res.withdrawalAmountInr;
          }
        }
      }
    });

    return response;

    // return Promise.resolve({
    //   status: false,
    // });
  }

  getPayoutsByPartner(filters: InvoicesListingFilters): Promise<Payout[]> {
    return Promise.resolve(
      this.payouts.filter((value, index) => {
        return (
          value.partnerId != undefined &&
          filters.partnerIds.indexOf(value.partnerId) > -1
        );
      }),
    );
  }

  private getTransactionType(
    invoice: Partial<Invoice>,
  ): 'goods' | 'services' | 'software' {
    // Map the transaction type string to InvoiceTransactionType
    if (!invoice.transactionType) {
      return 'services'; // Default to 'services' if no transaction type is specified
    }

    const transactionType = invoice.transactionType.toLowerCase();
    switch (transactionType) {
      case 'goods':
        return 'goods';
      case 'services':
        return 'services';
      case 'software':
        return 'software';
      default:
        return 'services'; // Default fallback
    }
  }
}
